import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { celebrateMilestone } from '@/utils/confetti';

interface LevelData {
  level: number;
  levelName: string;
  currentXP: number;
  xpForNextLevel: number;
  progress: number;
}

const XP_THRESHOLDS = [
  { level: 1, name: 'Bronze', minXP: 0, maxXP: 99 },
  { level: 2, name: 'Silver', minXP: 100, maxXP: 299 },
  { level: 3, name: 'Gold', minXP: 300, maxXP: 599 },
  { level: 4, name: 'Platinum', minXP: 600, maxXP: 999 },
  { level: 5, name: 'Diamond', minXP: 1000, maxXP: 1499 },
  { level: 6, name: 'Elite', minXP: 1500, maxXP: Infinity },
];

export const useXP = () => {
  const { user } = useAuth();
  const [levelData, setLevelData] = useState<LevelData | null>(null);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [newLevel, setNewLevel] = useState<{ level: number; name: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLevelData(null);
      setLoading(false);
      return;
    }

    fetchUserXP();
  }, [user]);

  const fetchUserXP = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('users')
        .select('xp_points, level, level_name')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching XP:', error);
      } else if (data) {
        const xp = data.xp_points || 0;
        const levelInfo = calculateLevelData(xp);
        setLevelData(levelInfo);
      }
    } catch (error) {
      console.error('Error in fetchUserXP:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateLevelData = (xp: number): LevelData => {
    const currentThreshold = XP_THRESHOLDS.find(
      (t) => xp >= t.minXP && xp <= t.maxXP
    ) || XP_THRESHOLDS[0];

    const nextThreshold = XP_THRESHOLDS[currentThreshold.level] || currentThreshold;
    const xpInCurrentLevel = xp - currentThreshold.minXP;
    const xpNeededForLevel = nextThreshold.minXP - currentThreshold.minXP;
    const progress = xpNeededForLevel > 0 ? (xpInCurrentLevel / xpNeededForLevel) * 100 : 100;

    return {
      level: currentThreshold.level,
      levelName: currentThreshold.name,
      currentXP: xp,
      xpForNextLevel: nextThreshold.minXP,
      progress: Math.min(progress, 100),
    };
  };

  const addXP = async (amount: number) => {
    if (!user || !levelData) return;

    const oldLevel = levelData.level;
    const newXP = levelData.currentXP + amount;

    try {
      const { error } = await supabase
        .from('users')
        .update({ xp_points: newXP })
        .eq('id', user.id);

      if (error) {
        console.error('Error adding XP:', error);
      } else {
        const newLevelData = calculateLevelData(newXP);
        setLevelData(newLevelData);

        // Check for level up
        if (newLevelData.level > oldLevel) {
          setNewLevel({ level: newLevelData.level, name: newLevelData.levelName });
          setShowLevelUp(true);
          celebrateMilestone();
        }
      }
    } catch (error) {
      console.error('Error in addXP:', error);
    }
  };

  const closeLevelUp = () => {
    setShowLevelUp(false);
    setNewLevel(null);
  };

  return {
    levelData,
    loading,
    addXP,
    showLevelUp,
    newLevel,
    closeLevelUp,
    refetch: fetchUserXP,
  };
};
